import jsPDF from "jspdf";

export const generateTaxFormPDF = (formQuestions, answers, comments, formNumber, companyLogo, memberType, memberIndex, signatureData = null) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const margin = {
    left: 15,
    right: 15,
    top: 25,  
    bottom: 20
  };

  const contentWidth = pageWidth - margin.left - margin.right;
  const headerStyles = {
    logo: { width: 40, height: 15, marginRight: 10 },
    title: {
      size: 16,
      style: 'bold',
      color: [0, 51, 102], 
      marginBottom: 3
    },
    formNumber: {
      size: 14,
      style: 'bold',
      color: [102, 0, 51] 
    },
    divider: { color: [200, 200, 200], width: 0.5 }
  };

  // Document styles
  const styles = {
    section: { size: 12, style: 'bold', color: [0, 51, 102], background: [230, 242, 255] },
    question: { size: 9.5, style: 'normal', color: [40, 40, 40] },
    answer: { size: 9.5, style: 'bold', colorYes: [0, 128, 0], colorNo: [192, 0, 0], colorNa: [102, 102, 102], colorDefault: [0, 0, 0] },
    comment: { size: 9, style: 'italic', color: [90, 90, 90] }
  };

  // Line heights
  const lineHeights = {
    title: 8,
    section: 6,
    question: 5,
    answer: 5,
    comment: 4
  };

  // Current position
  let y = margin.top;

  // Add header to each page
  const addHeader = () => {
    // Reset styles
    doc.setFont("helvetica");
    doc.setTextColor(0, 0, 0);

    // Calculate positions
    const logoX = margin.left;
    const logoY = 10;

    // Add logo
    doc.addImage(
      companyLogo,
      "PNG",
      logoX,
      logoY,
      headerStyles.logo.width,
      headerStyles.logo.height
    );

    // Calculate title position to center it properly
    const titleText = "Tax Filing Checklist";
    doc.setFontSize(headerStyles.title.size);
    doc.setFont("helvetica", headerStyles.title.style);
    doc.setTextColor(...headerStyles.title.color);

    const titleWidth = doc.getStringUnitWidth(titleText) * headerStyles.title.size / doc.internal.scaleFactor;
    const titleX = (pageWidth - titleWidth) / 2;
    const titleY = logoY + headerStyles.logo.height / 2 + headerStyles.title.size / 2;

    doc.text(titleText, titleX, titleY);

    // Add form number on the right
    const formText = `Form: ${formNumber}`;
    doc.setFontSize(headerStyles.formNumber.size);
    doc.setFont("helvetica", headerStyles.formNumber.style);
    doc.setTextColor(...headerStyles.formNumber.color);

    const formX = pageWidth - margin.right - 5;
    const formY = logoY + headerStyles.logo.height / 2 + headerStyles.formNumber.size / 2;

    doc.text(formText, formX, formY, { align: 'right' });

    // Add divider line below header
    const dividerY = titleY + headerStyles.title.marginBottom;
    doc.setDrawColor(...headerStyles.divider.color);
    doc.setLineWidth(headerStyles.divider.width);
    doc.line(margin.left, dividerY, pageWidth - margin.right, dividerY);

    // Reset y position for content (after header)
    y = dividerY + 5;
  };

  // Add footer to each page
  const addFooter = () => {
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100);

    // Footer line
    doc.setDrawColor(200, 200, 200);
    doc.line(margin.left, pageHeight - 15, pageWidth - margin.right, pageHeight - 15);

    // Page number
    doc.text(
      `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
      pageWidth - margin.right - 5,
      pageHeight - 10,
      { align: 'right' }
    );
  };

  // Check if we need a new page
  const checkNewPage = (requiredSpace) => {
    if (y + requiredSpace > pageHeight - margin.bottom) {
      addFooter();
      doc.addPage();
      y = margin.top;
      addHeader();
    }
  };

  // Add text with proper wrapping
  const addText = (text, options = {}) => {
    const {
      size = 10,
      style = 'normal',
      indent = 0,
      color = [0, 0, 0],
      maxLines = null
    } = options;

    doc.setFontSize(size);
    doc.setFont("helvetica", style);
    doc.setTextColor(...color);

    const x = margin.left + indent;
    const lineHeight = size * 0.35;
    const splitText = doc.splitTextToSize(text, contentWidth - indent);

    const textToAdd = maxLines ? splitText.slice(0, maxLines) : splitText;

    for (let i = 0; i < textToAdd.length; i++) {
      checkNewPage(lineHeight);
      doc.text(textToAdd[i], x, y);
      y += lineHeight;
    }

    if (maxLines && splitText.length > maxLines) {
      return splitText.slice(maxLines).join(' ');
    }

    return null;
  };

  // Add header to first page
  addHeader();

  // Process each section with clearer, colorful layout
  Object.keys(formQuestions).forEach((section, sectionIndex) => {
    const sectionLetter = String.fromCharCode(65 + sectionIndex);
    const sectionTitle = `${sectionLetter}. ${section}`;

    // Add section title with colored band
    checkNewPage(lineHeights.section * 3);
    const sectionBandHeight = 8;
    doc.setFillColor(...styles.section.background);
    doc.setDrawColor(255, 255, 255);
    doc.rect(margin.left, y, contentWidth, sectionBandHeight, "F");

    doc.setFontSize(styles.section.size);
    doc.setFont("helvetica", styles.section.style);
    doc.setTextColor(...styles.section.color);
    doc.text(sectionTitle, margin.left + 2, y + sectionBandHeight - 2);

    y += sectionBandHeight + 2;

    // Process each question
    formQuestions[section].forEach((question, questionIndex) => {
      const answer = answers[`${section}-${questionIndex}`] || 'Not answered';
      const comment = comments[`${section}-${questionIndex}`] || '';

      // Ensure space for one question block
      checkNewPage(20);

      // Light card background for each question
      const questionStartY = y;
      const cardPadding = 2;
      const cardWidth = contentWidth;

      // Question text
      const questionText = `${questionIndex + 1}. ${question}`;
      const questionLines = doc.splitTextToSize(questionText, cardWidth - cardPadding * 2);
      doc.setFontSize(styles.question.size);
      doc.setFont("helvetica", styles.question.style);
      doc.setTextColor(...styles.question.color);
      let currentY = questionStartY + cardPadding + styles.question.size * 0.35;
      questionLines.forEach((line) => {
        doc.text(line, margin.left + cardPadding, currentY);
        currentY += styles.question.size * 0.35;
      });

      // Answer row
      const labelWidth = 22;
      const answerY = currentY + 2;
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(120, 120, 120);
      doc.text("ANSWER", margin.left + cardPadding, answerY);

      const answerStr = String(answer || "").toLowerCase();
      const selectedOptions = answerStr.split(",");
      const isYes = selectedOptions.includes("yes");
      const isNo = selectedOptions.includes("no");
      const isNa = selectedOptions.includes("na");
      const isComment = selectedOptions.includes("comment");

      let displayAnswer = (answer || "Not answered").toUpperCase().replace(",", " & ");
      
      let answerColor = styles.answer.colorDefault;
      if (isYes) answerColor = styles.answer.colorYes;
      else if (isNo) answerColor = styles.answer.colorNo;
      else if (isNa) answerColor = styles.answer.colorNa;
      else if (answerStr === "not answered") answerColor = [150, 150, 150];

      doc.setFontSize(styles.answer.size);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...answerColor);
      doc.text(displayAnswer, margin.left + cardPadding + labelWidth, answerY);

      currentY = answerY + 4;

      // Comment row (if any)
      if (isComment && comment) {
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(120, 120, 120);
        doc.text("COMMENT", margin.left + cardPadding, currentY);

        doc.setFontSize(styles.comment.size);
        doc.setFont("helvetica", styles.comment.style);
        doc.setTextColor(...styles.comment.color);
        const commentLines = doc.splitTextToSize(comment, cardWidth - cardPadding * 2 - labelWidth);
        let commentY = currentY;
        commentLines.forEach((line) => {
          doc.text(line, margin.left + cardPadding + labelWidth, commentY);
          commentY += styles.comment.size * 0.35;
        });
        currentY = commentY;
      }

      // Draw card border around the block
      const cardHeight = currentY - questionStartY + cardPadding;
      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.2);
      doc.rect(margin.left, questionStartY, cardWidth, cardHeight);

      y = questionStartY + cardHeight + 2;
    });

    // Space between sections
    y += lineHeights.section;
  });

  // Add date section if signature data is provided
  if (signatureData && signatureData.signatureDate) {
    // Ensure enough space for date section
    checkNewPage(50);
    
    // Add some space before date section
    y += 10;
    
    // Add divider line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(margin.left, y, pageWidth - margin.right, y);
    y += 10;
    
    // Add declaration section title
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 51, 102);
    doc.text("Declaration", margin.left, y);
    y += 10;
    
    // Add declaration text
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    const declarationText = "I hereby declare that the information provided in this checklist is true, accurate, and complete to the best of my knowledge.";
    const splitDeclaration = doc.splitTextToSize(declarationText, contentWidth);
    splitDeclaration.forEach(line => {
      doc.text(line, margin.left, y);
      y += 5;
    });
    y += 8;

    // --- Date ---
    const rowStartY = y;
    const dateX = margin.left;

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("Date:", dateX, rowStartY);

    const formattedDate = new Date(signatureData.signatureDate).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const dateTextY = rowStartY + 5;
    doc.text(formattedDate, dateX, dateTextY);

    const dateLineY = dateTextY + 2;
    doc.setLineWidth(0.3);
    doc.line(dateX, dateLineY, dateX + 80, dateLineY);

    // Update y position for any content after
    y = dateLineY + 8;
  }

  // Add footer to last page
  addFooter();

  return doc.output("blob");
};